import React from "react";
import { useTranslation } from "react-i18next";
import { formatCash, formatCashFull } from "utils/formatCash";
import type { CashFlowSummary } from "hooks/useCashFlowSummary";
import TrendPanel from "components/common/TrendPanel";

interface BalancePanelProps {
  /** Cash across the company's own accounts. */
  balance: number;
  balanceLoading?: boolean;
  cashFlow: CashFlowSummary;
}

/**
 * The balance headline over its curve: what the company holds, and what moved
 * in and out of it over the chart's window.
 */
const BalancePanel: React.FC<BalancePanelProps> = ({
  balance,
  balanceLoading = false,
  cashFlow,
}) => {
  const { t } = useTranslation();
  const { series, currency, inflow, outflow, loading } = cashFlow;

  return (
    <TrendPanel
      title={t("Outpave balance")}
      value={formatCashFull(balance, currency)}
      valueLoading={balanceLoading}
      caption={t("Last 30 days")}
      trend={
        loading
          ? undefined
          : [
              {
                direction: "up",
                sentiment: "positive",
                text: t("{{amount}} in", {
                  amount: formatCash(inflow, currency),
                }),
              },
              {
                direction: "down",
                sentiment: "negative",
                text: t("{{amount}} out", {
                  amount: formatCash(outflow, currency),
                }),
              },
            ]
      }
      series={series.map((point) => ({
        date: point.date,
        value: point.balance,
      }))}
      formatValue={(value) => formatCashFull(value, currency)}
      loading={loading}
    />
  );
};

export default BalancePanel;
